// The site in the reader's language (docs/I18N.md). Text is written in English in the code and
// marked: t('Cash out') for a string, <T msg="Read the <a>Terms</a>" tags={…} /> for text with
// markup in it, msg('…') for a string defined once and translated where it's shown. Each language's
// catalog (locales/<lang>/web.json, and legal.json for the Terms and Privacy pages) maps the English
// to its translation; anything missing shows in English. scripts/i18n.mjs finds the marked strings
// and checks the catalogs.
//
// The language: ?lang= in the address, then the one the visitor picked (the gp_lang cookie, which
// the web Worker and the API read too), then the browser's languages, then English. Changing it
// re-renders the whole app (main.tsx keys the router by it).
import { Fragment, useSyncExternalStore, type ReactNode } from 'react';
import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  LIVE_LOCALES,
  LOCALE_INFO,
  LOCALES,
  contextKey,
  formatNumber,
  formatParts,
  isLocale,
  localeFromCookie,
  matchLocale,
  msg,
  type Catalog,
  type Locale,
} from '@gaintplay/core/i18n';

export { LOCALE_INFO, LOCALES, msg, type Locale };

/** web: the whole site but the legal pages; legal: the Terms and the Privacy Policy. */
export type Domain = 'web' | 'legal';

const loaders = import.meta.glob<Catalog>('./locales/*/*.json', { import: 'default' });

/**
 * The languages offered: English, and each live one (LIVE_LOCALES) whose site catalog is in the
 * build: the picker, their pre-rendered pages, search engines' links.
 */
export const AVAILABLE: readonly Locale[] = LOCALES.filter(
  (l) => l === DEFAULT_LOCALE || (LIVE_LOCALES.includes(l) && `./locales/${l}/web.json` in loaders),
);
const available = (l: unknown): l is Locale => isLocale(l) && AVAILABLE.includes(l);

let current: Locale = DEFAULT_LOCALE;
const catalogs = new Map<string, Catalog>();
/** Parts loaded so far: a language change loads them again in the new language. */
const inUse = new Set<Domain>(['web']);
const listeners = new Set<() => void>();

export const getLocale = () => current;

/** What goes between two sentences shown as separate messages: a space, except in Japanese. */
export const gap = () => (current === 'ja' ? '' : ' ');

/** Loads one part of a language's catalog (nothing for English, the language of the code). */
export async function loadCatalog(locale: Locale, domain: Domain): Promise<void> {
  inUse.add(domain);
  const key = `${locale}/${domain}`;
  if (locale === DEFAULT_LOCALE || catalogs.has(key)) return;
  const load = loaders[`./locales/${key}.json`];
  if (load) catalogs.set(key, await load());
}

function lookup(key: string): string | undefined {
  if (current === DEFAULT_LOCALE) return undefined;
  return catalogs.get(`${current}/web`)?.[key] ?? catalogs.get(`${current}/legal`)?.[key];
}

/**
 * A message's text in the reader's language before its values are filled in: for markup built
 * outside React that fills them in later (the FAQ's question count, which the web Worker writes).
 */
export const pattern = (message: string): string => lookup(message) ?? message;

type Values = Record<string, string | number>;

/** `message` (English) in the reader's language, with its values filled in. */
export function t(message: string, values?: Values): string {
  return formatParts<string>(current, lookup(message) ?? message, values).join('');
}

/**
 * Like t, for English that means different things in different places: the context tells the
 * translator which ("status" for "Open" as in "still open", "action" for the button).
 */
export function tc(context: string, message: string, values?: Values): string {
  return formatParts<string>(current, lookup(contextKey(context, message)) ?? message, values).join(
    '',
  );
}

type TagRender = (children: ReactNode) => ReactNode;
/** Markup every message may use without passing it: <b>, <i>, <br/>. */
const BUILT_IN: Record<string, TagRender> = {
  b: (c) => <strong className="font-bold">{c}</strong>,
  i: (c) => <em>{c}</em>,
  br: () => <br />,
};

/**
 * A message with markup: tags in the English (<a>Terms</a>, <b>1,000 coins</b>) are rendered by
 * `tags` (or the built-in b, i and br), and `values` can be React nodes. Values never become
 * markup, so a username like "<b>" shows as typed.
 *   <T msg="Email <mail>{address}</mail> from your account" values={{ address }}
 *      tags={{ mail: (c) => <a href={…}>{c}</a> }} />
 */
export function T({
  msg: message,
  ctx,
  values,
  tags,
}: {
  msg: string;
  ctx?: string;
  values?: Record<string, ReactNode>;
  tags?: Record<string, TagRender>;
}) {
  const pattern = lookup(ctx ? contextKey(ctx, message) : message) ?? message;
  let n = 0;
  const parts = formatParts<ReactNode>(
    current,
    pattern,
    values as Record<string, string | number | ReactNode> | undefined,
    (name, children) => {
      const render = tags?.[name] ?? BUILT_IN[name];
      const inner = children && (
        <>
          {children.map((c, i) => (
            <Fragment key={i}>{c}</Fragment>
          ))}
        </>
      );
      return <Fragment key={`t${n++}`}>{render ? render(inner) : inner}</Fragment>;
    },
  );
  return <>{parts.map((p, i) => (typeof p === 'string' ? p : <Fragment key={i}>{p}</Fragment>))}</>;
}

/** The reader's language; components using it re-render when it changes. */
export function useLocale(): Locale {
  return useSyncExternalStore(
    (f) => {
      listeners.add(f);
      return () => listeners.delete(f);
    },
    () => current,
    () => current,
  );
}

/* ------------------------------------------------------------------ Choosing the language */

function writeCookie(locale: Locale) {
  try {
    document.cookie = `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax${
      location.protocol === 'https:' ? '; Secure' : ''
    }`;
  } catch {
    /* cookies blocked: the choice lasts until the page is closed */
  }
}

/** ?lang=, the picked language, the browser's, English (the web Worker picks the same way). */
export function detectLocale(): Locale {
  const asked = new URLSearchParams(location.search).get('lang');
  if (available(asked)) {
    writeCookie(asked);
    return asked;
  }
  const picked = localeFromCookie(document.cookie);
  if (available(picked)) return picked;
  const browser = navigator.languages?.length ? navigator.languages : [navigator.language];
  const matched = matchLocale(browser);
  return available(matched) ? matched : DEFAULT_LOCALE;
}

function apply(locale: Locale) {
  current = locale;
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

// In the browser the language is known as soon as this module loads, so everything that runs
// before the first render (the boot prefetch, its query keys) already asks in it.
if (typeof document !== 'undefined') apply(detectLocale());

/** Before the first render: the language's catalog, so nothing shows in English first. */
export async function startI18n(): Promise<void> {
  await loadCatalog(current, 'web').catch(() => {});
}

/** The visitor picked a language: kept (cookie), loaded, and the app re-renders in it. */
export async function setLocale(locale: Locale): Promise<void> {
  writeCookie(locale);
  await Promise.all([...inUse].map((d) => loadCatalog(locale, d)));
  apply(locale);
  for (const f of listeners) f();
}

/** For the build's pre-render: render in `locale` (its catalogs loaded first). */
export function renderIn(locale: Locale) {
  apply(locale);
}

/* ------------------------------------------------------------------ Numbers and dates */

/** The Intl tag for the reader's language ("de-DE"), for formatting this file doesn't cover. */
export const intlLocale = () => LOCALE_INFO[current].intl;

export const fmtNumber = (n: number, options?: Intl.NumberFormatOptions) =>
  formatNumber(current, n, options);

const dateFormats = new Map<string, Intl.DateTimeFormat>();
/** A date or time in the reader's language: fmtDate(t, { dateStyle: 'medium' }). */
export function fmtDate(
  t: number | Date,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const key = `${current}|${JSON.stringify(options)}`;
  let f = dateFormats.get(key);
  if (!f) dateFormats.set(key, (f = new Intl.DateTimeFormat(intlLocale(), options)));
  return f.format(t);
}

const relativeFormats = new Map<string, Intl.RelativeTimeFormat>();
/** Narrow reads as a bare "-26 min" in French and Swedish ("vor 26 m" in German): these take short. */
const SHORT_AGO: readonly Locale[] = ['de', 'fr', 'sv'];
/** How long ago, short: "5m ago", "vor 3 Min.", "il y a 3 min", "12秒前". */
export function fmtAgo(t: number, now = Date.now()): string {
  let f = relativeFormats.get(current);
  if (!f)
    relativeFormats.set(
      current,
      (f = new Intl.RelativeTimeFormat(intlLocale(), {
        style: SHORT_AGO.includes(current) ? 'short' : 'narrow',
      })),
    );
  const s = Math.max(1, Math.floor((now - t) / 1000));
  if (s < 60) return f.format(-s, 'second');
  const m = Math.floor(s / 60);
  if (m < 60) return f.format(-m, 'minute');
  const h = Math.floor(m / 60);
  if (h < 24) return f.format(-h, 'hour');
  return f.format(-Math.floor(h / 24), 'day');
}
